import { API_CONFIG, fetchAPI } from './api';

export type ContactSocialResult = {
  platform?: string;
  profile_url?: string;
  title?: string;
  snippet?: string;
  confidence?: number;
  source_url?: string;
  name?: string;
  company_name?: string;
  location?: string;
  personal_email?: string;
  business_email?: string;
  raw?: any;
};

export type ContactSocialSearchPayload = {
  account_id?: string;
  name: string;
  company_name?: string;
  location?: string;
  personal_email?: string;
  business_email?: string;
  platforms?: string[];
  max_results?: number;
};

export async function startContactSocials(payload: ContactSocialSearchPayload) {
  return fetchAPI<{ success: boolean; run_id?: string; status?: string; error?: string }>(
    `${API_CONFIG.RESEARCH_API}/contact-socials`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
}

export async function checkContactSocialsStatus(runId: string) {
  return fetchAPI<{ success: boolean; status?: string; total?: number; succeeded?: number; failed?: number }>(
    `${API_CONFIG.RESEARCH_API}/contact-socials/status`,
    {
      method: 'POST',
      body: JSON.stringify({ run_id: runId }),
    }
  );
}

export async function getContactSocialsResults(runId: string) {
  return fetchAPI<{ success: boolean; results?: ContactSocialResult[] }>(
    `${API_CONFIG.RESEARCH_API}/contact-socials/results`,
    {
      method: 'POST',
      body: JSON.stringify({ run_id: runId }),
    }
  );
}

export async function pollContactSocialsResults(
  runId: string,
  onProgress?: (progress: number, message: string) => void
) {
  let status = 'RUNNING';
  let attempts = 0;
  const maxAttempts = 60; // 3 minutes max

  while (status === 'RUNNING' && attempts < maxAttempts) {
    await new Promise((resolve) => setTimeout(resolve, 3000));
    attempts++;
    const out = await checkContactSocialsStatus(runId);
    status = out.status ?? 'RUNNING';
    const progress = Math.min(20 + (attempts / maxAttempts) * 70, 95);
    onProgress?.(progress, `Searching socials... (${attempts * 3}s)`);
    if (status === 'SUCCEEDED') break;
    if (status === 'FAILED') throw new Error('Contact socials failed');
  }

  if (attempts >= maxAttempts) throw new Error('Contact socials timeout');
  const results = await getContactSocialsResults(runId);
  return results;
}
